import { useState, useEffect } from 'react';

/**
 * useIsTouch — reactive "is this a touch device?" check.
 *
 * Keyed on `(pointer: coarse)` rather than a width breakpoint, because the
 * question a row actually needs answered is "can this person swipe and are
 * their targets fingers?", not "how wide is the window?". A tablet in
 * landscape is wide and still wants touch-sized controls; a narrow desktop
 * window is not a phone.
 *
 * Guarded for jsdom and SSR, where matchMedia may be missing entirely, and it
 * listens for changes so plugging in a mouse takes effect without a reload.
 *
 * (Daily Operations has the same check inline as `useIsMobile`; that file also
 * pulls in its Firestore subscriptions, so it isn't importable from here.)
 */
export const TOUCH_MEDIA_QUERY = '(pointer: coarse)';

export default function useIsTouch() {
  const read = () => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(TOUCH_MEDIA_QUERY).matches
      : false
  );
  const [touch, setTouch] = useState(read);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(TOUCH_MEDIA_QUERY);
    const onChange = e => setTouch(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange); // Safari < 14
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return touch;
}
